/**
 * Auto-reload on a Vite dynamic-import (chunk preload) failure.
 *
 * After a deploy the SPA's hashed chunk filenames change and the previous ones are removed from S3, so
 * a tab still running the OLD bundle fails to lazy-load a route chunk. On demo/dev, CloudFront serves
 * `index.html` (200, `text/html`) for the now-missing `/assets/*.js`, so the failure surfaces as
 * "Failed to fetch dynamically imported module" / a MIME error and a white screen. Vite fires a
 * `vite:preloadError` event for exactly this; reloading fetches the fresh `index.html` + new chunks and
 * the stale tab self-heals.
 *
 * Loop-guarded: only auto-reload if we haven't already within {@link RELOAD_COOLDOWN_MS} — a genuinely
 * broken fresh bundle would otherwise reload forever. The marker is sessionStorage so it survives the
 * reload; the cooldown lets a LATER deploy trigger a fresh reload.
 */
import { markReload } from './reloadReason';

const RELOAD_MARKER_KEY = 'vite:preloadError:lastReload';
export const RELOAD_COOLDOWN_MS = 10_000;

/** Pure decision (extracted for testing): reload only if the last auto-reload is outside the cooldown. */
export function shouldReloadOnPreloadError(now: number, lastReloadRaw: string | null): boolean {
  const last = Number(lastReloadRaw ?? 0);
  if (!Number.isFinite(last)) return true;
  return now - last >= RELOAD_COOLDOWN_MS;
}

/** Wire the global `vite:preloadError` → one-shot (cooldown-guarded) page reload. Call once at startup. */
export function registerPreloadErrorAutoReload(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    let last: string | null = null;
    try { last = sessionStorage.getItem(RELOAD_MARKER_KEY); } catch { /* private mode / quota */ }
    if (!shouldReloadOnPreloadError(Date.now(), last)) return;
    try { sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now())); } catch { /* non-fatal */ }
    // B3 (2026-09-13): say WHY before reloading — the relay log line + the boot marker make a
    // stale-bundle reload distinguishable from a consent/auth reload or the user's own F5.
    const payload = (event as { payload?: { message?: unknown } }).payload;
    const message = typeof payload?.message === 'string' ? payload.message.slice(0, 160) : '';
    markReload('preload-error', message ? `error="${message}"` : undefined);
    window.location.reload();
  });
}
